import BarChart from "components/charts/BarChart";
import LineChart from "components/charts/LineChart";
import PieChart from "components/charts/PieChart";
import { format } from "date-fns";
import { subMonths } from "date-fns/esm";
import useDonationReport from "hook/reports/useDonationReport";
import { DONATION_REPORT } from "navigation/routes";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";

const DonationAnalytics = () => {
  const { dummy_data, dummy_line_data, dummy_line2_data, dummy_pie_data, selectedYear, setSelectedYear } = useDonationReport()

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  const downloadPDF = (e, filename) => {
    e.preventDefault();
    const card = e.target.closest('.card');
    const canvas = card.querySelector('canvas');
    if (canvas) {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${filename}.pdf`);
    }
  };

  const downloadChart = (e, filename) => {
    e.preventDefault();
    const card = e.target.closest('.card');
    const canvas = card.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = url;
      link.click();
    }
  };

  return (
    <>

      <div className="container-fluid px-4">
        <div className="d-sm-flex align-items-center justify-content-between mt-4 mb-4">
          <div>
            <h1>Donation Analytics</h1>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><Link to={`../${DONATION_REPORT}`}>Donations</Link></li>
              <li className="breadcrumb-item active">Analytics</li>
            </ol>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="yearFilter" className="mb-0 text-nowrap">Filter Year:</label>
              <select
                id="yearFilter"
                className="form-select form-select-sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                style={{ width: '100px' }}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <Link className="btn btn-primary btn-sm" to={`../${DONATION_REPORT}`} relative="path">View List</Link>
          </div>
        </div>
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-lg-6">
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-chart-bar me-1"></i>
                    {selectedYear} Donations by Month
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={(e) => downloadChart(e, `Donations_by_Month_${selectedYear}`)}>
                      <i className="fas fa-image"></i> Image
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={(e) => downloadPDF(e, `Donations_by_Month_${selectedYear}`)}>
                      <i className="fas fa-file-pdf"></i> PDF
                    </button>
                  </div>
                </div>                <div className="card-body">
                  <BarChart data={dummy_data} />
                </div>
                <div className="card-footer small text-muted">
                  Showing monthly distribution for {selectedYear}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-chart-pie me-1"></i>
                    Total Donations by Cycle for {selectedYear}
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={(e) => downloadChart(e, `Total_Donations_By_Cycle_${selectedYear}`)}>
                      <i className="fas fa-image"></i> Image
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={(e) => downloadPDF(e, `Total_Donations_By_Cycle_${selectedYear}`)}>
                      <i className="fas fa-file-pdf"></i> PDF
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <PieChart data={dummy_pie_data} />
                </div>
                <div className="card-footer small text-muted">
                  Categorized by donation frequency
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-chart-line me-1"></i>
                    Donations for Current Month ({format(new Date(), 'MMM yyyy')})
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={(e) => downloadChart(e, `Donations_${format(new Date(), 'MMM_yyyy')}`)}>
                      <i className="fas fa-image"></i> Image
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={(e) => downloadPDF(e, `Donations_${format(new Date(), 'MMM_yyyy')}`)}>
                      <i className="fas fa-file-pdf"></i> PDF
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <LineChart data={dummy_line_data} />
                </div>
                <div className="card-footer small text-muted">
                  Daily breakdown
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-chart-line me-1"></i>
                    Donations for Last Month ({format(subMonths(new Date(), 1), 'MMM yyyy')})
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={(e) => downloadChart(e, `Donations_${format(subMonths(new Date(), 1), 'MMM_yyyy')}`)}>
                      <i className="fas fa-image"></i> Image
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={(e) => downloadPDF(e, `Donations_${format(subMonths(new Date(), 1), 'MMM_yyyy')}`)}>
                      <i className="fas fa-file-pdf"></i> PDF
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <LineChart data={dummy_line2_data} />
                </div>
                <div className="card-footer small text-muted">
                  Daily breakdown
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DonationAnalytics;
